<template>
  <component
    :is="triggerType"
    class="shop-product-card"
    :class="{
      'shop-product-card--selected': selected || value.selected,
      'shop-product-card--active': active,
      'shop-product-card--locked': !status.isSelectable,
    }"
    v-on="$listeners"
    :href="`/shop/product/${value.id}`"
    target="_blank"
  >
    <div class="shop-product-card__inner-wrapper">
      <div class="shop-product-card__content-wrapper">
        <div v-if="selected" class="shop-product-card__icon">
          <Icon v-bind="icon" />
        </div>
        <div class="shop-product-card__image">
          <img v-if="image" :src="image" />
          <Icon v-else icon="fa-pro-light:box-alt" size="lg" />
        </div>

        <div class="shop-product-card__content">
          <div class="shop-product-card__name">
            <span> {{ value.product.name }} </span>
            <Icon
              v-if="showProductLink && triggerType === 'a'"
              icon="fa-pro-light:external-link"
              class="shop-product-card__product-link-icon"
            />
          </div>
          <div
            v-if="selectedVariant"
            class="shop-product-card__variant shop-product-card__property"
          >
            {{ $tc('variants', 1) }}: {{ selectedVariant.name }}
          </div>
          <div v-if="!value.isDummy" class="shop-product-card__price shop-product-card__property">
            <span v-if="value.isDefault">{{ $t('standard') }}</span>
            <span v-else-if="[null, undefined].includes(value.product.gross)">
              {{ $t('priceOnRequest') }}</span
            >
            <span v-else>
              <template>{{ Currency(value.product.gross) }}</template>
              <template v-if="$path('quantity.enabled', value)">
                / {{ $t('piecesShort') }}</template
              >
            </span>
          </div>
        </div>
      </div>
      <Quantity :value="value" @change="q => $emit('quantity', q)" />
    </div>
  </component>
</template>

<script>
import { Currency } from '../utils'
import { InputComponent } from 'vue-elder-input'
import Quantity from '../components/Quantity.vue'
import Icon from '@kvass/vue2-icon'

export default {
  props: {
    value: Object,
    subcategory: Object,
    showProductLink: Boolean,
    active: Boolean,
    selected: Boolean,
    currency: String,
  },
  inject: {
    getItemStatus: {
      default: null,
    },
  },
  computed: {
    variants() {
      return this.$path('value.product.variants.items') || []
    },
    selectedVariant() {
      if (!this.variants.length) return
      const { value, category } = this.value.variant || {}
      const { name, internalId } =
        this.variants.find(v => (value ? v.internalId === value : v.isDefault)) || {}

      return {
        category:
          category && this.value.product.variants.category === category
            ? category
            : this.value.product.variants.category,
        name,
        internalId,
      }
    },
    triggerType() {
      if (!this.showProductLink || !this.value.id) return 'button'
      return 'a'
    },
    image() {
      return this.$path('value.product.thumbnail.url') || this.$path('value.product.images.0.url')
    },
    status() {
      if (!this.getItemStatus) return { isSelectable: true }
      return this.getItemStatus(this.value.id)
    },
    icon() {
      if (!this.status.isUnselectable && !this.active) return { icon: 'fa-pro-light:lock-alt' }
      if (!this.status.isUnselectable && this.active)
        return { icon: 'fa-pro-solid:lock-alt', size: 'sm', style: 'translate: 0 -1px' }
      return { icon: 'fa-pro-solid:check' }
    },
  },
  methods: {
    Currency(val) {
      return Currency(val, { currency: this.currency })
    },
  },
  components: {
    InputComponent,
    Icon,
    Quantity,
  },
}
</script>

<style lang="scss">
@import '../mixins';

.shop-product-card {
  text-decoration: none;

  position: relative;
  cursor: pointer;
  width: 100%;
  color: inherit;

  display: flex;
  flex-direction: column;

  text-align: left;

  &:focus-visible {
    --color-accent: #{lighten(#a7a7a7, 28%)};
  }

  &:not(&--selected):hover {
    --color-accent: #{lighten(#a7a7a7, 33%)};
  }

  &--selected {
    --color-accent: #{lighten(#a7a7a7, 30%)};

    margin: 0;
    border-width: 1px;
    border-color: var(--color);
  }

  &--locked:not(&--selected) {
    color: rgba(black, 0.6);
    background-color: rgba(black, 0.04);
    pointer-events: none;

    .shop-product-card__image {
      opacity: 0.8;
    }
  }

  .shop-subcategory--active &--selected {
    --color: var(--primary);
    --color-accent: #{css-alpha('primary', 0.1)};
  }

  &__price {
    opacity: 0.5;
  }

  &__property {
    svg {
      margin-right: 3px;
    }
  }

  &__inner-wrapper {
    position: relative;

    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    width: 100%;
  }

  &__content-wrapper {
    display: flex;
    align-items: center;
  }

  &__icon {
    position: absolute;
    top: 0;
    right: 0;
    color: var(--color);

    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;

    .shop-subcategory--active & {
      background-color: var(--primary);
      color: white;
      width: 20px;
      height: 20px;
      font-size: 0.8em;
    }
  }

  .shop-product-quantity {
    align-self: flex-end;
  }

  &__image {
    $size: 65px;

    width: $size;
    height: $size;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid $border-color;
    border-radius: var(--border-radius);

    background-color: rgba(black, 0.05);

    margin-right: 1.25rem;

    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: inherit;
      object-position: center;
    }

    svg {
      opacity: 0.3;
    }
  }

  &__name {
    display: flex;
    gap: 0.5rem;

    align-items: center;
  }

  &__product-link-icon {
    opacity: 0.7;
    font-size: 0.8em;
    overflow: visible;

    &:hover {
      cursor: pointer;
    }
  }

  &__price {
    white-space: nowrap;
  }
}
</style>
